import React, { useState } from "react";
import "../../assets/css/UserTable.css";
import {
  FaEdit,
  FaTrash,
  FaClock,
  FaSignInAlt,
  FaSignOutAlt,
} from "react-icons/fa";

function UserTable({ users, deleteUser, updateUser }) {
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});

  // Utility functions for formatting timestamps
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Never";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "Never";
    const now = new Date();
    const date = new Date(timestamp);
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 2592000)
      return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  const calculateSessionDuration = (loginTime, logoutTime) => {
    if (!loginTime) return "N/A";
    if (!logoutTime) return "Active";

    const login = new Date(loginTime);
    const logout = new Date(logoutTime);
    const duration = Math.floor((logout - login) / 1000);

    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = duration % 60;

    if (hours > 0) return `${hours}h ${minutes}m`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    setFormData(user);
  };

  const handleSave = () => {
    updateUser(editingId, formData);
    setEditingId(null);
    setFormData({});
  };
  const handleCancel = () => {
    setEditingId(null);
    setFormData({});
  };
  return (
    <table className="user-table">
      <thead>
        <tr>
          <th>First Name</th>
          <th>Last Name</th>
          <th>Email</th>
          <th>Role</th>
          <th>Status</th>
          <th>
            <FaSignInAlt /> Last Login
          </th>
          <th>
            <FaSignOutAlt /> Last Logout
          </th>
          <th>
            <FaClock /> Session Duration
          </th>
          <th>Last Activity</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {users.map((u, index) => (
          <tr key={u.id || u.email || index}>
            <td>
              {editingId === u.id ? (
                <input
                  value={formData.firstName}
                  onChange={(e) =>
                    setFormData({ ...formData, firstName: e.target.value })
                  }
                />
              ) : (
                u.firstName
              )}
            </td>
            <td>
              {editingId === u.id ? (
                <input
                  value={formData.lastName}
                  onChange={(e) =>
                    setFormData({ ...formData, lastName: e.target.value })
                  }
                />
              ) : (
                u.lastName
              )}
            </td>
            <td>
              {editingId === u.id ? (
                <input
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              ) : (
                u.email
              )}
            </td>
            <td>
              {editingId === u.id ? (
                <select
                  value={formData.role}
                  onChange={(e) =>
                    setFormData({ ...formData, role: e.target.value })
                  }
                >
                  <option value="admin">Admin</option>
                  <option value="users">Users</option>
                </select>
              ) : (
                u.role
              )}
            </td>
            <td>
              {editingId === u.id ? (
                <select
                  value={formData?.status || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              ) : (
                  u?.status ? u?.status : null
              )}
            </td>
            <td className="activity-cell">
              <div className="timestamp-info">
                <div className="timestamp-full">
                  {formatTimestamp(u.loginTimestamp)}
                </div>
                <div className="timestamp-relative">
                  {formatRelativeTime(u.loginTimestamp)}
                </div>
              </div>
            </td>
            <td className="activity-cell">
              <div className="timestamp-info">
                <div className="timestamp-full">
                  {formatTimestamp(u.logoutTimestamp)}
                </div>
                <div className="timestamp-relative">
                  {formatRelativeTime(u.logoutTimestamp)}
                </div>
              </div>
            </td>
            <td className="activity-cell">
              <span
                className={`session-duration ${
                  u.status === "active" ? "active-session" : ""
                }`}
              >
                {calculateSessionDuration(u.loginTimestamp, u.logoutTimestamp)}
              </span>
            </td>
            <td className="activity-cell">
              <div className="timestamp-info">
                <div className="timestamp-full">
                  {formatTimestamp(u.lastActivity)}
                </div>
                <div className="timestamp-relative">
                  {formatRelativeTime(u.lastActivity)}
                </div>
              </div>
            </td>
            <td className="user-table-actions">
              {editingId === u.id ? (
                <>
                  <button onClick={handleSave} className="btn-save">
                    <span>Save Changes</span>
                  </button>
                  <p></p>
                  <button onClick={handleCancel} className="btn-cancel">
                    <span>Cancel</span>
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => handleEdit(u)} aria-label="Edit user">
                    <FaEdit />
                    <span>Edit</span>
                  </button>
                  <p></p>
                  <button
                    onClick={() => deleteUser(u.id, u)}
                    className="btn-danger"
                    aria-label="Delete user"
                  >
                    <FaTrash />
                    <span>Delete</span>
                  </button>
                </>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default UserTable;
